import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';

import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  faClone,
  faCopy,
  faPen,
  faPlus,
  faQuestion,
  faRotate,
  faSave,
  faStar,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';

import { SettingService } from '../setting.service';
import { AlertService } from '../alert.service';

import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { SettingsEmailsLogsComponent } from '../settings-emails-logs/settings-emails-logs.component';
import { EmailTemplateModalComponent } from './email-template-modal/email-template-modal.component';

type EmailTemplateFilterType = EmailTemplateType | 'ALL';

export type EmailTemplateType =
  | 'PROPOSAL'
  | 'INVOICE'
  | 'PAYMENT'
  | 'REVIEW'
  | 'NOTIFICATION'
  | 'GENERIC';

export interface EmailTemplate {
  id: string;
  organizationId?: string;
  name: string;
  type: EmailTemplateType;
  subject: string;
  body: string;
  isDefault: boolean;
  createdAt?: number;
  updatedAt?: number;
}

@Component({
  selector: 'app-settings-emails',
  templateUrl: './settings-emails.component.html',
  styleUrls: ['./settings-emails.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbTooltip,
    FaIconComponent,
    SettingsEmailsLogsComponent,
  ],
})
export class SettingsEmailsComponent implements OnInit, OnDestroy {
  emailSettingsForm: UntypedFormGroup;
  prospectEmail = 'Not Configured';

  templates: EmailTemplate[] = [];
  selectedTemplateType: EmailTemplateFilterType = 'ALL';

  templateTypes: { label: string; value: EmailTemplateType }[] = [
    { label: 'Proposal', value: 'PROPOSAL' },
    { label: 'Invoice', value: 'INVOICE' },
    { label: 'Payment', value: 'PAYMENT' },
    { label: 'Review', value: 'REVIEW' },
    { label: 'Notification', value: 'NOTIFICATION' },
    { label: 'Generic', value: 'GENERIC' },
  ];

  faRotate = faRotate;
  faCopy = faCopy;
  faSave = faSave;
  faQuestion = faQuestion;
  faStar = faStar;
  faPen = faPen;
  faClone = faClone;
  faTrash = faTrash;
  faPlus = faPlus;

  constructor(
    private settingService: SettingService,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.emailSettingsForm = this.formBuilder.group({
      displayName: [''],
      replyTo: [''],
    });

    this.getEmailSendingSettings();
    this.getEmailReceivingSettings();
    this.getEmailTemplates();
  }

  ngOnDestroy(): void {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
  }

  getEmailTemplates(): void {
    this.settingService.getEmailTemplates(this.selectedTemplateType).subscribe({
      next: (templates) => {
        this.templates = templates ?? [];
      },
      error: () => {
        this.alertService.showErrorAlert('Unable to load email templates');
      },
    });
  }

  openCreateTemplateModal(): void {
    const modalRef = this.modalService.open(EmailTemplateModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.mode = 'create';
    modalRef.componentInstance.template = {
      id: '',
      name: '',
      type:
        this.selectedTemplateType === 'ALL'
          ? 'PROPOSAL'
          : this.selectedTemplateType,
      subject: '',
      body: '',
      isDefault: false,
    };

    modalRef.closed.subscribe((template: EmailTemplate) => {
      this.settingService
        .createEmailTemplate({
          name: template.name,
          type: template.type,
          subject: template.subject,
          body: template.body,
          isDefault: template.isDefault,
        })
        .subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Email Template Created');
            this.getEmailTemplates();
          },
          error: () => {
            this.alertService.showErrorAlert('Unable to create template');
          },
        });
    });
  }

  openEditTemplateModal(template: EmailTemplate): void {
    const modalRef = this.modalService.open(EmailTemplateModalComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
    });

    modalRef.componentInstance.mode = 'edit';
    modalRef.componentInstance.template = { ...template };

    modalRef.closed.subscribe((updatedTemplate: EmailTemplate) => {
      this.settingService
        .updateEmailTemplate({
          id: updatedTemplate.id,
          name: updatedTemplate.name,
          type: updatedTemplate.type,
          subject: updatedTemplate.subject,
          body: updatedTemplate.body,
          isDefault: updatedTemplate.isDefault,
        })
        .subscribe({
          next: () => {
            this.alertService.showSuccessAlert('Email Template Updated');
            this.getEmailTemplates();
          },
          error: () => {
            this.alertService.showErrorAlert('Unable to update template');
          },
        });
    });
  }

  setAsDefaultTemplate(template: EmailTemplate): void {
    if (template.isDefault) return;

    this.settingService.setDefaultEmailTemplate(template.id).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Default Template Updated');
        this.getEmailTemplates();
      },
      error: () => {
        this.alertService.showErrorAlert('Unable to set default template');
      },
    });
  }

  duplicateTemplate(template: EmailTemplate): void {
    this.settingService.duplicateEmailTemplate(template.id).subscribe({
      next: () => {
        this.alertService.showSuccessAlert('Template Duplicated');
        this.getEmailTemplates();
      },
      error: () => {
        this.alertService.showErrorAlert('Unable to duplicate template');
      },
    });
  }

  deleteTemplate(template: EmailTemplate): void {
    if (template.isDefault) return;

    const confirmationModalRef = this.modalService.open(
      ConfirmationModalComponent
    );

    confirmationModalRef.componentInstance.message = `Are you sure that you want to delete "${template.name}"?`;
    confirmationModalRef.componentInstance.actionBtnTitle = 'Delete';
    confirmationModalRef.componentInstance.actionBtnType = 'DANGER';

    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.settingService.deleteEmailTemplate(template.id).subscribe({
        next: () => {
          this.alertService.showSuccessAlert('Email Template Deleted');
          this.getEmailTemplates();
        },
        error: () => {
          this.alertService.showErrorAlert('Unable to delete template');
        },
      });
    });
  }

  getBodyPreview(body: string): string {
    if (!body) return 'No description available';

    const div = document.createElement('div');
    div.innerHTML = body;

    const text = (div.textContent || div.innerText || '')
      .replace(/\s+/g, ' ')
      .trim();

    return text || 'No description available';
  }

  getTemplateTypeLabel(type: EmailTemplateType): string {
    return (
      this.templateTypes.find((item) => item.value === type)?.label ?? type
    );
  }

  formatUnixDate(value?: number): string {
    if (!value) return '—';

    return new Date(value * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private getEmailSendingSettings(): void {
    this.settingService.getEmailSendingSettings().subscribe((settings) => {
      this.emailSettingsForm.controls.displayName.setValue(
        settings?.displayName ?? ''
      );
      this.emailSettingsForm.controls.replyTo.setValue(settings?.replyTo ?? '');
    });
  }

  updateEmailSendingSettings(): void {
    if (!this.emailSettingsForm.valid) {
      this.emailSettingsForm.markAllAsTouched();
      return;
    }

    const data = {
      displayName: this.emailSettingsForm.value.displayName,
      replyTo: this.emailSettingsForm.value.replyTo,
    };

    this.settingService.updateEmailSendingSettings(data).subscribe(() => {
      this.alertService.showSuccessAlert('Email Sending Updated');
    });
  }

  private getEmailReceivingSettings(): void {
    this.settingService.getEmailReceivingSettings().subscribe((settings) => {
      this.prospectEmail =
        settings?.prospects?.emailAddress || 'Not Configured';
    });
  }

  async copyProspectsEmail(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.prospectEmail);
      this.alertService.showSuccessAlert('Prospects Email Copied');
    } catch {
      this.alertService.showErrorAlert('Unable to Copy Prospects Email');
    }
  }

  openRegenerateProspectsEmailConfirmationModal(): void {
    const confirmationModalRef = this.modalService.open(
      ConfirmationModalComponent
    );

    confirmationModalRef.componentInstance.message =
      'Are you sure that you want to regenerate the Prospects Email?';
    confirmationModalRef.componentInstance.actionBtnTitle = 'Confirm';
    confirmationModalRef.componentInstance.actionBtnType = 'PRIMARY';

    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.regenerateProspectsEmail();
    });
  }

  private regenerateProspectsEmail(): void {
    this.settingService.regenerateProspectsEmail().subscribe(() => {
      this.alertService.showSuccessAlert('Prospects Email Regenerated');
      this.getEmailReceivingSettings();
    });
  }
}